import { useMutation, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"

import { supabase } from "@/lib/supabase"
import { getSupabaseFunctionErrorMessage } from "@/utils/supabaseFunctionUtils"

import type { TablesUpdate } from "../../database.types"

interface UpdatePersonParams {
  id: string
  fields: TablesUpdate<"people">
}

export function useUpdatePerson() {
  const queryClient = useQueryClient()

  const updateMutation = useMutation({
    mutationFn: async ({ id, fields }: UpdatePersonParams) => {
      const fieldsWithTimestamp = {
        ...fields,
        updated_at: new Date().toISOString(),
      }

      const { data, error } = await supabase.functions.invoke("update-person-supabase", {
        body: {
          id,
          fields: fieldsWithTimestamp,
        },
      })

      if (error) {
        throw new Error(await getSupabaseFunctionErrorMessage(error))
      }

      if (!data.success) {
        throw new Error(data?.message || "Failed to update person")
      }

      if (data.cancelled_shift_count === null) {
        toast.warning("Shifts not cancelled", {
          description:
            "The person was updated, but their upcoming shifts could not be cancelled. Please cancel them manually.",
        })
      } else if (data.cancelled_shift_count > 0) {
        const count = data.cancelled_shift_count
        toast.success(`Cancelled ${count} upcoming shift${count > 1 ? "s" : ""}`)
        if (data.failed_cancellation_webhooks > 0) {
          toast.warning("Some cancellation notifications failed", {
            description: `${data.failed_cancellation_webhooks} of ${count} cancellation notifications could not be sent. The driver may need to be told directly.`,
          })
        }
      }

      if (data.cancelled_shift_count !== undefined) {
        queryClient.invalidateQueries({ queryKey: ["shifts"] })
        queryClient.invalidateQueries({ queryKey: ["activeEngagements", id] })
      }

      return data.data
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["people"] })
      queryClient.invalidateQueries({ queryKey: ["person", variables.id] })
    },
    onError: (error) => {
      toast.error("Update Failed", {
        description:
          error instanceof Error && error.message
            ? error.message
            : "Failed to update person. Please try again.",
      })
    },
  })

  return {
    updatePerson: updateMutation.mutateAsync,
    isUpdating: updateMutation.isPending,
    updateError: updateMutation.error,
  }
}
